/**
 * AI 分析服务
 * 负责与后端 AI 分析 API 交互
 */

import axios from 'axios';
import type {
  UserPreference,
  PromptTemplate,
  AIAnalysisHistory,
  AIAnalysisHistoryListItem,
  SingleAnalysisResult,
  ComparisonResult,
  RecommendationResult,
  AnalyzeSingleRequest,
  CompareKeywordsRequest,
  RecommendKeywordsRequest,
} from '../types/ai-analysis-types';

// 安全地获取环境变量，处理 undefined 情况
const getApiBaseUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
  }
  return 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // AI 分析可能需要较长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证 token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== 用户偏好 API ====================

/**
 * 获取当前用户的偏好配置
 */
export async function getMyPreference(): Promise<UserPreference> {
  const response = await apiClient.get<UserPreference>('/user-preferences/my_preference/');
  return response.data;
}

/**
 * 保存或更新用户偏好配置
 */
export async function savePreference(data: UserPreference): Promise<UserPreference> {
  const response = await apiClient.post<UserPreference>('/user-preferences/save_preference/', data);
  return response.data;
}

// ==================== 提示语模板 API ====================

/**
 * 获取所有提示语模板
 */
export async function getPromptTemplates(): Promise<PromptTemplate[]> {
  const response = await apiClient.get<PromptTemplate[]>('/prompt-templates/');
  return response.data;
}

/**
 * 根据类型获取提示语模板
 */
export async function getPromptTemplateByType(type: string): Promise<PromptTemplate> {
  const response = await apiClient.get<PromptTemplate>(`/prompt-templates/get_by_type/?type=${type}`);
  return response.data;
}

/**
 * 创建提示语模板
 */
export async function createPromptTemplate(data: PromptTemplate): Promise<PromptTemplate> {
  const response = await apiClient.post<PromptTemplate>('/prompt-templates/', data);
  return response.data;
}

/**
 * 更新提示语模板
 */
export async function updatePromptTemplate(id: number, data: Partial<PromptTemplate>): Promise<PromptTemplate> {
  const response = await apiClient.patch<PromptTemplate>(`/prompt-templates/${id}/`, data);
  return response.data;
}

/**
 * 删除提示语模板
 */
export async function deletePromptTemplate(id: number): Promise<void> {
  await apiClient.delete(`/prompt-templates/${id}/`);
}

// ==================== AI 分析 API ====================

/**
 * 分析单个关键词
 */
export async function analyzeSingleKeyword(data: AnalyzeSingleRequest): Promise<SingleAnalysisResult> {
  const response = await apiClient.post<SingleAnalysisResult>('/ai-analysis/analyze_single/', data);
  return response.data;
}

/**
 * 对比多个关键词
 */
export async function compareKeywords(data: CompareKeywordsRequest): Promise<ComparisonResult> {
  const response = await apiClient.post<ComparisonResult>('/ai-analysis/compare/', data);
  return response.data;
}

/**
 * 智能推荐关键词
 */
export async function recommendKeywords(data: RecommendKeywordsRequest): Promise<RecommendationResult> {
  const response = await apiClient.post<RecommendationResult>('/ai-analysis/recommend/', data);
  return response.data;
}

// ==================== AI 分析历史 API ====================

/**
 * 获取 AI 分析历史列表
 */
export async function getAnalysisHistory(): Promise<AIAnalysisHistoryListItem[]> {
  const response = await apiClient.get<AIAnalysisHistoryListItem[]>('/ai-history/');
  return response.data;
}

/**
 * 获取单条 AI 分析历史详情
 */
export async function getAnalysisHistoryDetail(id: number): Promise<AIAnalysisHistory> {
  const response = await apiClient.get<AIAnalysisHistory>(`/ai-history/${id}/`);
  return response.data;
}

// ==================== 错误处理工具 ====================

/**
 * 解析 API 错误信息
 */
export function parseApiError(error: any): string {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const data = error.response.data;
      if (typeof data === 'object' && data !== null) {
        if ('error' in data) {
          return data.error;
        }
        if ('detail' in data) {
          return data.detail;
        }
        // 返回第一个字段的错误
        const firstKey = Object.keys(data)[0];
        if (firstKey) {
          const value = data[firstKey];
          return Array.isArray(value) ? value[0] : value;
        }
      }
      return `请求失败: ${error.response.status}`;
    }
    if (error.request) {
      return '网络错误，请检查连接';
    }
  }
  return error.message || '未知错误';
}

const aiAnalysisService = {
  getMyPreference,
  savePreference,
  getPromptTemplates,
  getPromptTemplateByType,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
  analyzeSingleKeyword,
  compareKeywords,
  recommendKeywords,
  getAnalysisHistory,
  getAnalysisHistoryDetail,
  parseApiError,
};

export default aiAnalysisService;

